import * as React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { validateInput } from '../../pages/util/validateFormInput';
import useScrollPosition from '../../hooks/useScrollPosition';
import { useTranslation } from 'react-i18next';

const BackOfficeConfigurationForm = () => {

  const theme = useTheme();
  const scrollPosition = useScrollPosition();
  const { t } = useTranslation();

  const isMobile = useMediaQuery(theme.breakpoints.between('xs', 'md'));
  const [formSubmitted, setFormSubmitted] = React.useState(false);

  const [formData, setFormData] = React.useState({
    channel_id: '',
    travel_agency_name: '',
    iata_number: '',
    agency_ipAddress: '',
    agency_url: '',
    agency_email: '',
    agency_userId: '',
    agency_password: ''
  });

  const [formValidation, setFormValidation] = React.useState({
    channel_id: false,
    travel_agency_name: false,
    iata_number: false,
    agency_ipAddress: false,
    agency_url: false,
    agency_email: false,
    agency_userId: false,
    agency_password: false
  });

  const [formError, setFormError] = React.useState({
    channel_id: false,
    travel_agency_name: false,
    iata_number: false,
    agency_ipAddress: false,
    agency_url: false,
    agency_email: false,
    agency_userId: false,
    agency_password: false
  });

  const channels = [
    "aircanada.com/agents",
    "NDC",
    "AC Connex"
  ]

  const [showSelect, setShowSelect] = React.useState(false);

  React.useEffect(() => {
    if (scrollPosition) {
      setShowSelect(false)
    }
  }, [scrollPosition]);

  const isValidForm = !Object.values(formValidation).every((item) => item);

  const handleFormData = (e: any) => {
    const trimmedValue =
      ['travel_agency_name', 'agency_userId', 'agency_password'].includes(e.target.name)
        ? e.target.value.trimStart() : e.target.value;

    setFormData((prevData) => ({
      ...prevData,
      [e.target.name]: trimmedValue
    }));

    const validateValue =
      ['travel_agency_name', 'agency_userId', 'agency_password'].includes(e.target.name)
        ? trimmedValue.trim()
        : trimmedValue;

    const op = validateInput(
      {
        content: validateValue,
        validate: e.target.id
      },
      t
    );
    setFormError((prevData) => ({
      ...prevData,
      [e.target.name]: op.msg
    }));
    setFormValidation((prevData) => ({
      ...prevData,
      [e.target.name]: !op.error
    }));
  };

  const handleSubmit = async () => {
    console.log('form is submitted successfully..!');
    setFormSubmitted(true);
  };

  return (
    <Box sx={{ display: 'flex', mt: '1rem' }}>
      {formSubmitted ? (
        <Box
          sx={{
            bgcolor: '#f1f7fa',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '86vh',
            width: '100%'
          }}
        >
          <Paper
            elevation={3}
            sx={{
              py: '2rem',
              px: isMobile ? '1rem' : '5rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '40rem',
              mx: isMobile ? '1rem' : 0
            }}
          >
            <Typography
              variant="h5"
              fontWeight={600}
              mb={3}
              textAlign={'center'}
            >
              {t('backOfficeConfigurationForm.conclude.title')}
            </Typography>
          </Paper>
        </Box>
      ) : (
        <Grid container columnGap={7}>
          <Grid item xs={12} sm={12} md={4} lg={4} bgcolor="#f1f7fa" p={4}>
            <Typography variant="h5" mb={2} fontWeight={600}>
              {t('backOfficeConfigurationForm.sidebar.title')}
            </Typography>
            <Typography
              fontSize={'.875rem'}
              mb={2}
              fontWeight={600}
              color="#d8292f"
            >
              {t('backOfficeConfigurationForm.sidebar.note')}
            </Typography>
            <Typography fontSize={'.875rem'} mb={2}>
              {t('backOfficeConfigurationForm.sidebar.subtitle.0')}
            </Typography>
            <Typography fontSize={'.875rem'}>
              {t('backOfficeConfigurationForm.sidebar.subtitle.1')}
            </Typography>
          </Grid>
          <Grid
            item
            container
            xs={12}
            sm={12}
            md={7}
            lg={7}
            pt={4}
            sx={{ ...(isMobile && { mx: 4 }) }}
          >
            <Grid item xs={12} sm={12} md={6} lg={6} mb={isMobile ? 5 : 27}>
              <Typography fontWeight={600} fontSize={'20px'} gutterBottom>
                {t('backOfficeConfigurationForm.form.title')}
              </Typography>
              <FormControl variant="standard"
                fullWidth sx={{ mt: 2 }}
                error={Boolean(formError.channel_id)}
              >
                <InputLabel
                  htmlFor="channel_id"
                  required
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 400,
                    lineHeight: '1.25rem',
                    color: '#C1C5C8'
                  }}
                >
                  {t('backOfficeConfigurationForm.form.field1.data')}
                </InputLabel>
                <Select
                  labelId="channel_id"
                  required
                  id="text"
                  name="channel_id"
                  value={formData.channel_id}
                  onChange={handleFormData}
                  aria-describedby="channel-id-error"
                  sx={{
                    fontSize: '.9rem',
                    fontWeight: 400,
                    lineHeight: '1.25rem',
                    color: '#000',
                    py: '.2rem'
                  }}
                  inputProps={{
                    style: {
                      borderBottom: `1px solid #C1C5C8`
                    }
                  }}
                  open={showSelect}
                  onOpen={() => setShowSelect(true)}
                  onClose={() => setShowSelect(false)}
                  MenuProps={{}}
                >
                  {channels.map((item, index) => (
                    <MenuItem
                      key={index}
                      value={item}
                    >
                      {item}
                    </MenuItem>
                  ))}
                </Select>
                {formError.channel_id && <FormHelperText id="channel-id-error">{formError.channel_id}</FormHelperText>}
              </FormControl>
              <TextField
                variant="standard"
                id="text"
                name="travel_agency_name"
                value={formData.travel_agency_name}
                onChange={handleFormData}
                error={Boolean(formError.travel_agency_name)}
                helperText={formError.travel_agency_name}
                InputLabelProps={{
                  style: { fontSize: '1rem', color: '#C1C5C8' }
                }}
                sx={{ mt: 2.5, width: '100%' }}
                required
                size="small"
                label={t('backOfficeConfigurationForm.form.field2.data')}
                inputProps={{
                  style: {
                    padding: '.4rem 0'
                  }
                }}
              />
              <TextField
                variant="standard"
                id="iata"
                name="iata_number"
                value={formData.iata_number}
                onChange={handleFormData}
                error={Boolean(formError.iata_number)}
                helperText={
                  formError.iata_number || t('backOfficeConfigurationForm.form.field3.helperText')
                }
                InputLabelProps={{
                  style: { fontSize: '1rem', color: '#C1C5C8' }
                }}
                sx={{ mt: 2.5, width: '100%' }}
                required
                size="small"
                label={t('backOfficeConfigurationForm.form.field3.data')}
                inputProps={{
                  style: {
                    padding: '.4rem 0'
                  }
                }}
              />
              <TextField
                variant="standard"
                id="ipAddress"
                name="agency_ipAddress"
                value={formData.agency_ipAddress}
                onChange={handleFormData}
                error={Boolean(formError.agency_ipAddress)}
                helperText={formError.agency_ipAddress}
                InputLabelProps={{
                  style: { fontSize: '1rem', color: '#C1C5C8' }
                }}
                sx={{ mt: 2.5, width: '100%' }}
                required
                size="small"
                label={t('backOfficeConfigurationForm.form.field4.data')}
                inputProps={{
                  style: {
                    padding: '.4rem 0'
                  }
                }}
              />
              <TextField
                variant="standard"
                id="url"
                name="agency_url"
                value={formData.agency_url}
                onChange={handleFormData}
                error={Boolean(formError.agency_url)}
                helperText={formError.agency_url}
                InputLabelProps={{
                  style: { fontSize: '1rem', color: '#C1C5C8' }
                }}
                sx={{ mt: 2.5, width: '100%' }}
                required
                size="small"
                label={t('backOfficeConfigurationForm.form.field5.data')}
                inputProps={{
                  style: {
                    padding: '.4rem 0'
                  }
                }}
              />
              <TextField
                variant="standard"
                id="email"
                name="agency_email"
                value={formData.agency_email}
                onChange={handleFormData}
                error={Boolean(formError.agency_email)}
                helperText={formError.agency_email}
                InputLabelProps={{
                  style: { fontSize: '1rem', color: '#C1C5C8' }
                }}
                sx={{ mt: 2.5, width: '100%' }}
                required
                size="small"
                label={t('backOfficeConfigurationForm.form.field6.data')}
                inputProps={{
                  style: {
                    padding: '.4rem 0'
                  }
                }}
              />
              <TextField
                variant="standard"
                id="text"
                name="agency_userId"
                value={formData.agency_userId}
                onChange={handleFormData}
                error={Boolean(formError.agency_userId)}
                helperText={formError.agency_userId}
                InputLabelProps={{
                  style: { fontSize: '1rem', color: '#C1C5C8' }
                }}
                sx={{ mt: 2.5, width: '100%' }}
                required
                size="small"
                label={t('backOfficeConfigurationForm.form.field7.data')}
                inputProps={{
                  style: {
                    padding: '.4rem 0'
                  }
                }}
              />
              <TextField
                variant="standard"
                id="text"
                name="agency_password"
                value={formData.agency_password}
                onChange={handleFormData}
                error={Boolean(formError.agency_password)}
                helperText={formError.agency_password}
                InputLabelProps={{
                  style: { fontSize: '1rem', color: '#C1C5C8' }
                }}
                sx={{ mt: 2.5, width: '100%' }}
                required
                size="small"
                label={t('backOfficeConfigurationForm.form.field8.data')}
                inputProps={{
                  style: {
                    padding: '.4rem 0'
                  }
                }}
              />
            </Grid>
            <Grid
              item
              container
              xs={12}
              sm={12}
              md={6}
              lg={6}
              mb={isMobile ? 7 : 15}
              alignItems={'end'}
              justifyContent={'center'}
            >
              <Button
                onClick={handleSubmit}
                aria-label={t('backOfficeConfigurationForm.form.submit')}
                sx={{
                  borderRadius: 3,
                  height: 'fit-content',
                  width: isMobile ? '100%  ' : '13rem',
                  bgcolor: '#005078',
                  color: theme.palette.primary.contrastText,
                  '&:hover': { backgroundColor: '#005078' },
                  boxShadow: '0 0 1rem #ccc',
                  textTransform: 'none',
                  fontSize: '1rem',
                  ...(isValidForm && {
                    bgcolor: '#D0D0CE',
                    color: '#737373',
                    boxShadow: 'none'
                  })
                }}
                disabled={isValidForm}
              >
                {t('backOfficeConfigurationForm.form.submit')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default BackOfficeConfigurationForm;